import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { connect } from 'react-redux';
import { Translate, I18n } from 'react-redux-i18n';
import * as actions from 'redux/actions/marketWinLowActions';
import Tabs from 'components/Tabs';
import MiniLoader from 'components/MiniLoader';
import StockAvatar from 'components/StockAvatar';

function formatPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return '—';
  }
  const abs = Math.abs(n).toFixed(2);
  return `${n > 0 ? '+' : n < 0 ? '-' : ''}${abs}%`;
}

function formatPrice(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) {
    return '—';
  }
  return n >= 1000 ? n.toFixed(0) : n >= 100 ? n.toFixed(1) : n.toFixed(2);
}

function Week52Range({ item }) {
  const low = Number(item.week52_low);
  const high = Number(item.week52_high);
  const position = Number(item.range_position);

  if (!(low > 0 && high > low && Number.isFinite(position))) {
    return null;
  }

  const title = `${formatPrice(low)} – ${formatPrice(high)}`;

  return (
    <div className="stock-movers-52w" title={title}>
      <span className="stock-movers-52w-label">
        <Translate value="stock_movers_52w" />
      </span>
      <div className="stock-movers-52w-track" aria-hidden="true">
        <span
          className="stock-movers-52w-marker"
          style={{ left: `${position}%` }}
        />
      </div>
      <span className="stock-movers-52w-pct">{Math.round(position)}%</span>
    </div>
  );
}

function MoverRow({ item, tone }) {
  const symbol = String(item.symbol || '').toUpperCase();
  if (!symbol) {
    return null;
  }

  return (
    <RouterLink to={`/stock/${encodeURIComponent(symbol)}`} className={`stock-movers-row is-${tone}`}>
      <StockAvatar item={item} symbol={symbol} className="stock-movers-avatar" />
      <div className="stock-movers-main">
        <div className="stock-movers-top">
          <span className="stock-movers-symbol">{symbol}</span>
          <span className="stock-movers-pct">{formatPct(item.porcent)}</span>
        </div>
        <Week52Range item={item} />
      </div>
    </RouterLink>
  );
}

function MoverList({ list, loading, error, tone }) {
  const rows = Array.isArray(list) ? list.filter((item) => item?.symbol) : [];

  if (loading && !rows.length) {
    return (
      <div className="stock-movers-state">
        <MiniLoader />
      </div>
    );
  }

  if (error && !rows.length) {
    return (
      <div className="stock-movers-state stock-movers-state--error">
        <Translate value="stock_movers_error" />
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="stock-movers-state">
        <Translate value="stock_movers_empty" />
      </div>
    );
  }

  return (
    <div className="stock-movers-list">
      {rows.slice(0, 15).map((item) => (
        <MoverRow
          key={item.id || item.symbol}
          item={item}
          tone={tone}
        />
      ))}
    </div>
  );
}

const MarketWinLow = (props) => {
  useEffect(() => {
    props.loadingWinners({ id: props.user_id }, props.token);
    props.loadingLosers({ id: props.user_id }, props.token);
  }, [props.user_id]);

  return (
    <div className="stock-movers">
      <h2 className="stock-section-title">
        <Translate value="stock_movers_title" />
      </h2>
      <p className="stock-movers-subtitle">
        <Translate value="stock_movers_midcap_hint" />
        {' · '}
        <Translate value="stock_movers_52w_hint" />
      </p>
      <Tabs>
        <div label={I18n.t('stock_movers_winners')}>
          <MoverList
            list={props.listWinners}
            loading={props.loadingWinners}
            error={props.errorWinners}
            tone="up"
          />
        </div>
        <div label={I18n.t('stock_movers_losers')}>
          <MoverList
            list={props.listLosers}
            loading={props.loadingLosers}
            error={props.errorLosers}
            tone="down"
          />
        </div>
      </Tabs>
    </div>
  );
};

function mapStateToProps(reducer) {
  return {
    user_id: reducer.session.auth.id,
    token: reducer.session.auth.token,
    listWinners: reducer.marketWinLow.listWinners,
    loadingWinners: reducer.marketWinLow.loadingWinners,
    errorWinners: reducer.marketWinLow.errorWinners,
    loadingLosers: reducer.marketWinLow.loadingLosers,
    listLosers: reducer.marketWinLow.listLosers,
    errorLosers: reducer.marketWinLow.errorLosers,
  };
}

const mapDispatchToProps = (dispatch) => ({
  loadingWinners: (params, token) =>
    dispatch(actions.loadListWinners({ params, token })),
  loadingLosers: (params, token) =>
    dispatch(actions.loadListLosers({ params, token })),
});

export default connect(mapStateToProps, mapDispatchToProps)(MarketWinLow);
